import { BaseApiService } from './baseApiService';
import type { ApprovedSignupPlan } from './billingService';

// Clinic Interfaces
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface Clinic {
  id: number;
  name: string;
  phone_no: string;
  email: string;
  abn?: string;
  address_line_1?: string;
  address_line_2?: string;
  city: string;
  zip_code?: string;
  country: string;
  logo?: string;
  is_approved: boolean | number;
  approval_status: ApprovalStatus;
  rejection_reason?: string;
  created_on?: string;
  updated_on?: string;
  admin_emails?: string[];
  approved_signup_plan: ApprovedSignupPlan | null;
}

export interface GetClinicsParams {
  status?: ApprovalStatus | 'all';
  search?: string;
  city?: string;
  country?: string;
  page?: number;
  limit?: number;
}

export interface ClinicsListResponse {
  status: string;
  message: string;
  data: Clinic[];
}

export interface ClinicDetailResponse {
  status: string;
  message: string;
  data: Clinic;
}

export interface ApprovalRequest {
  approval_status: 'approved';
}

export interface RejectionRequest {
  approval_status: 'rejected';
  rejection_reason: string;
}

export interface ApprovalResponse {
  status?: string; // Optional since backend may not return this
  message: string;
  data: Clinic;
}

// Clinic Profile Interfaces
export interface ClinicProfileData {
  name: string;
  phone_no: string;
  email: string;
  url?: string;
  abn: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  zip_code: string;
  country: string;
  logo?: string;
}

export interface UpdateClinicRequest {
  name?: string;
  phone_no?: string;
  email?: string;
  url?: string;
  abn?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  zip_code?: string;
  country?: string;
  logo?: string;
}

export interface UpdateClinicResponse {
  status: string;
  message: string;
  data: ClinicProfileData;
}

// Clinic Branding Interfaces (EHR Backend)
export interface ClinicBrandingData {
  id: number | null;
  clinic_id: number;
  display_name: string | null;
  subtitle: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  moh_registration_number: string | null;
  website: string | null;
  config_metadata: Record<string, unknown>;
}

export interface UpdateBrandingRequest {
  display_name?: string | null;
  subtitle?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  moh_registration_number?: string | null;
  website?: string | null;
}

export interface WorkflowReviewPolicy {
  tier3_second_reviewer_required: boolean;
  updated_by: number | null;
  updated_at: string | null;
}

const reviewPolicyOf = (metadata: Record<string, unknown> | undefined): WorkflowReviewPolicy => {
  const stored = (metadata?.workflow_review_policy ?? {}) as Partial<WorkflowReviewPolicy>;
  return {
    tier3_second_reviewer_required: stored.tier3_second_reviewer_required === true,
    updated_by: typeof stored.updated_by === 'number' ? stored.updated_by : null,
    updated_at: typeof stored.updated_at === 'string' ? stored.updated_at : null,
  };
};

interface BrandingResponse {
  status: string;
  message: string;
  data: ClinicBrandingData;
}

// WhatsApp Channel (Auth Backend — /clinics/{id}/whatsapp-channel/)
export type WhatsAppProvider = 'twilio' | 'meta';
export type WhatsAppChannelStatus =
  'draft' | 'pending_verification' | 'connected' | 'error' | 'disabled';

export interface WhatsAppChannel {
  id: number;
  provider: WhatsAppProvider;
  integration_mode: string;
  status: WhatsAppChannelStatus;
  display_number: string | null;
  meta_phone_number_id: string | null;
  meta_waba_id: string | null;
  twilio_account_sid: string | null;
  twilio_messaging_service_sid: string | null;
  twilio_sender_number: string | null;
  last_verified_at: string | null;
  error_detail: string | null;
  metadata: Record<string, unknown>;
  has_access_token: boolean;
  has_twilio_auth_token: boolean;
  has_app_secret: boolean;
  has_webhook_verify_token: boolean;
}

export interface UpdateWhatsAppChannelRequest {
  provider: WhatsAppProvider;
  integration_mode?: string;
  display_number?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_sender_number?: string;
  twilio_messaging_service_sid?: string;
  meta_phone_number_id?: string;
  meta_waba_id?: string;
  access_token?: string;
  app_secret?: string;
  webhook_verify_token?: string;
}

interface WhatsAppChannelResponse {
  status: string;
  message: string;
  data?: WhatsAppChannel | null;
}

class ClinicService extends BaseApiService {
  /**
   * Get list of clinics with optional filtering
   * @param params - Query parameters for filtering clinics
   * @returns Promise with clinics list
   */
  async getClinics(params?: GetClinicsParams): Promise<Clinic[]> {
    const queryParams = new URLSearchParams();

    if (params?.status && params.status !== 'all') {
      queryParams.append('approval_status', params.status);
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.city) {
      queryParams.append('city', params.city);
    }
    if (params?.country) {
      queryParams.append('country', params.country);
    }

    // Backend has default limit of 10, so we send a high limit to get all records
    queryParams.append('limit', '1000');

    const queryString = queryParams.toString();
    const endpoint = `/clinics/?${queryString}`;

    const response = await this.fetchWithAuthUrl<ClinicsListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  }

  /**
   * Get detailed information for a specific clinic
   * @param clinicId - The ID of the clinic
   * @returns Promise with clinic details
   */
  async getClinicById(clinicId: number): Promise<Clinic> {
    const response = await this.fetchWithAuthUrl<ClinicDetailResponse>(
      `/clinics/${clinicId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  }

  /**
   * Approve a clinic
   * @param clinicId - The ID of the clinic to approve
   * @returns Promise with updated clinic data
   */
  async approveClinic(clinicId: number): Promise<Clinic> {
    const requestBody: ApprovalRequest = {
      approval_status: 'approved',
    };

    const response = await this.fetchWithAuthUrl<ApprovalResponse>(
      `/clinics/${clinicId}/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    );
    return response.data;
  }

  /**
   * Reject a clinic with a reason
   * @param clinicId - The ID of the clinic to reject
   * @param rejectionReason - The reason for rejection
   * @returns Promise with updated clinic data
   */
  async rejectClinic(
    clinicId: number,
    rejectionReason: string,
  ): Promise<Clinic> {
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      throw new Error('Rejection reason is required');
    }

    const requestBody: RejectionRequest = {
      approval_status: 'rejected',
      rejection_reason: rejectionReason.trim(),
    };

    const response = await this.fetchWithAuthUrl<ApprovalResponse>(
      `/clinics/${clinicId}/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    );
    return response.data;
  }

  /**
   * Get current admin's clinic profile
   * @param clinicId - The ID of the clinic
   * @returns Promise with wrapped clinic profile response
   */
  async getClinicProfile(clinicId: number): Promise<ClinicProfileData> {
    const response = await this.fetchWithAuthUrl<UpdateClinicResponse>(
      `/clinics/${clinicId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  }

  /**
   * Update current admin's clinic profile
   * @param clinicId - The ID of the clinic
   * @param updateData - Partial clinic data to update
   * @returns Promise with updated clinic data
   */
  async updateClinicProfile(
    clinicId: number,
    updateData: UpdateClinicRequest,
  ): Promise<ClinicProfileData> {
    const response = await this.fetchWithAuthUrl<UpdateClinicResponse>(
      `/clinics/${clinicId}/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      },
    );
    return response.data;
  }
  // === Clinic Branding (EHR Backend) ===

  async getClinicBranding(): Promise<ClinicBrandingData> {
    const response = await this.fetchWithAuth<BrandingResponse>(
      '/clinic/config/',
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return response.data;
  }

  async updateClinicBranding(
    data: UpdateBrandingRequest,
  ): Promise<ClinicBrandingData> {
    const response = await this.fetchWithAuth<BrandingResponse>(
      '/clinic/config/',
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return response.data;
  }

  async getWorkflowReviewPolicy(): Promise<WorkflowReviewPolicy> {
    return reviewPolicyOf((await this.getClinicBranding()).config_metadata);
  }

  async setWorkflowReviewPolicy(required: boolean): Promise<WorkflowReviewPolicy> {
    const response = await this.fetchWithAuth<BrandingResponse>(
      '/clinic/config/',
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_metadata: { workflow_review_policy: { tier3_second_reviewer_required: required } } }),
      },
    );
    return reviewPolicyOf(response.data.config_metadata);
  }

  async uploadClinicLogo(file: File): Promise<ClinicBrandingData> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.fetchWithAuth<BrandingResponse>(
      '/clinic/config/logo/',
      {
        method: 'POST',
        body: formData,
      },
    );
    return response.data;
  }

  async deleteClinicLogo(): Promise<ClinicBrandingData> {
    const response = await this.fetchWithAuth<BrandingResponse>(
      '/clinic/config/logo/',
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return response.data;
  }

  // === WhatsApp Channel (Auth Backend) ===

  async getWhatsAppChannel(clinicId: number): Promise<WhatsAppChannel | null> {
    const response = await this.fetchWithAuthUrl<WhatsAppChannelResponse>(
      `/clinics/${clinicId}/whatsapp-channel/`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    return response.data ?? null;
  }

  async updateWhatsAppChannel(
    clinicId: number,
    data: UpdateWhatsAppChannelRequest,
  ): Promise<WhatsAppChannel> {
    const response = await this.fetchWithAuthUrl<WhatsAppChannelResponse>(
      `/clinics/${clinicId}/whatsapp-channel/`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return response.data as WhatsAppChannel;
  }

  async verifyWhatsAppChannel(clinicId: number): Promise<WhatsAppChannel> {
    const response = await this.fetchWithAuthUrl<WhatsAppChannelResponse>(
      `/clinics/${clinicId}/whatsapp-channel/verify/`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    return response.data as WhatsAppChannel;
  }

  async disconnectWhatsAppChannel(clinicId: number): Promise<void> {
    await this.fetchWithAuthUrl(`/clinics/${clinicId}/whatsapp-channel/`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
  }
}

export const clinicService = new ClinicService();
export type { Clinic as ClinicType };
